import { Button, Stack, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/useAuth";
import { ACCOUNT_COPY, deleteAccountConfirmation } from "../../constants/account";
import { SUPPORT_EMAIL } from "../../constants/publicUrls";
import { usersApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { ConfirmDialog, ContentCard } from "../ui";

export function DeleteAccountCard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const confirmationValue = user ? deleteAccountConfirmation(user.email) : null;
  const confirmationValid = confirmationValue !== null && confirmation === confirmationValue;

  function closeDialog() {
    setOpened(false);
    setConfirmation("");
  }

  async function handleConfirm() {
    if (!confirmationValid) return;
    setLoading(true);
    try {
      await usersApi.requestDeletion();
      logout();
      navigate("/login");
      notifications.show({
        color: "green",
        title: ACCOUNT_COPY.deleteSuccessTitle,
        message: ACCOUNT_COPY.deleteSuccessBody,
      });
    } catch (err) {
      notifications.show({
        color: "red",
        title: ACCOUNT_COPY.deleteTitle,
        message: err instanceof ApiError ? err.message : "Could not close the account",
      });
    } finally {
      setLoading(false);
      closeDialog();
    }
  }

  return (
    <>
      <ContentCard>
        <Stack gap="md">
          <Title order={4}>{ACCOUNT_COPY.deleteTitle}</Title>
          <Text size="sm" c="dimmed">
            {ACCOUNT_COPY.deleteBody} {SUPPORT_EMAIL}
          </Text>
          <Button
            color="red"
            variant="default"
            onClick={() => {
              setConfirmation("");
              setOpened(true);
            }}
          >
            {ACCOUNT_COPY.deleteCta}
          </Button>
        </Stack>
      </ContentCard>
      <ConfirmDialog
        opened={opened}
        onClose={closeDialog}
        title={ACCOUNT_COPY.deleteConfirmTitle}
        description={
          <Text size="sm">
            {ACCOUNT_COPY.deleteConfirmationLabel}:{" "}
            <Text span fw="var(--axio-font-weight-semibold)">
              {confirmationValue}
            </Text>{" "}
            {ACCOUNT_COPY.deleteConfirmationSuffix}
          </Text>
        }
        confirmLabel={ACCOUNT_COPY.deleteConfirmationCta}
        confirmDisabled={!confirmationValid}
        loading={loading}
        onConfirm={() => void handleConfirm()}
      >
        <TextInput
          label={ACCOUNT_COPY.deleteConfirmationLabel}
          value={confirmation}
          onChange={(event) => setConfirmation(event.currentTarget.value)}
          disabled={loading || confirmationValue === null}
        />
      </ConfirmDialog>
    </>
  );
}
